import { useCallback, useEffect, useMemo, useState } from 'react'

import { useLinkTo } from '@react-navigation/native'
import { pdf } from '@react-pdf/renderer'
import { saveAs } from 'file-saver'
import {
  DIRECTORY_TYPE_ENUM,
  EXTENSION_TYPE_ENUM,
  useUploadMutation
} from 'integration/resources/upload'
import {
  TManageWalletParams,
  TStaffListItem,
  TStoresListItem,
  useConcludeWalletsManagementMutation,
  useGetStaffListInfinityQuery,
  useGetStoresListInfinityQuery,
  useGetWalletsManagementDetailsQuery,
  useFlagAsExternalMutation,
  useManageWalletMutation,
  WALLET_MANAGEMENT_STATUS_ENUM,
  useReactivateWalletsPeriodMutation,
  useGetWalletsManagementReportQuery,
  useSaveReportFileMutation
} from 'integration/resources/walletsManagement'
import { useBreakpointValue, useDisclose, useToast } from 'native-base'
import { Toast } from 'organisms'
import useDebounce from 'src/hooks/useDebounce'
import { useAuthAtomValue } from 'src/store/auth'

import { OrderField, UseStaffListScreen } from './StaffListScreen.types'
import { ReportPDF } from '../Components/ReportPDF/ReportPDF'
import { TAB_ENUM } from '../LeadersList/LeadersListScreen.types'

export const orderFields: OrderField[] = [
  {
    label: 'Nome',
    options: [
      { id: 'asc', name: 'A-Z' },
      { id: 'desc', name: 'Z-A' }
    ]
  },
  {
    label: 'Número de TABs',
    options: [
      { id: 'asc', name: 'Menor para o Maior' },
      { id: 'desc', name: 'Maior para o Menor' }
    ]
  }
]

const manageFilters = [
  { id: 0, name: 'Todas' },
  { id: 1, name: 'Carteirizadas' },
  { id: 2, name: 'Não Carteirizadas' }
]

const manageFiltersStaff = [
  { id: 0, name: 'Todos' },
  { id: 1, name: 'Carteirizados' },
  { id: 2, name: 'Não Carteirizados' }
]

const mergeStaffInfiniteQuery = (data: ReturnType<typeof useGetStaffListInfinityQuery>['data']) =>
  data?.pages.reduce<TStaffListItem[]>(
    (previousValue, currentValue) => [...previousValue, ...currentValue.data.data],
    []
  ) ?? []

const mergeStoresInfiniteQuery = (data: ReturnType<typeof useGetStoresListInfinityQuery>['data']) =>
  data?.pages.reduce<TStoresListItem[]>(
    (previousValue, currentValue) => [...previousValue, ...currentValue.data.data],
    []
  ) ?? []

export const useStaffListScreen: UseStaffListScreen = ({ navigation, route }) => {
  const leaderStdCode = route?.params?.userStdCode

  const authAtomValue = useAuthAtomValue()

  const isAdmin = useMemo(
    () => !!(authAtomValue?.user?.is_admin && leaderStdCode),
    [authAtomValue?.user?.is_admin, leaderStdCode]
  )

  const isMobile = useBreakpointValue({ base: true, lg: false })

  const linkTo = useLinkTo()

  const [currentTab, setCurrentTab] = useState<TAB_ENUM>(TAB_ENUM.Specialists)

  const [timeouts, setTimeouts] = useState<number[]>([])

  const [searchStaff, onSearchStaff] = useState<string>('')

  const debouncedSearchStaff = useDebounce(searchStaff, 500)

  const [searchStore, onSearchStore] = useState<string>('')

  const debouncedSearchStore = useDebounce(searchStore, 500)

  const [searchManageStaff, onSearchManageStaff] = useState<string>('')

  const debouncedSearchManageStaff = useDebounce(searchManageStaff, 500)

  const [searchManageStore, onSearchManageStore] = useState<string>('')

  const debouncedSearchManageStore = useDebounce(searchManageStore, 500)

  const handleSearchClear = () => {
    onSearchStaff('')

    onSearchStore('')

    onSearchManageStaff('')

    onSearchManageStore('')
  }

  const [selectedOrderBy, setSelectedOrderBy] = useState<{
    field: string | undefined
    direction: 'asc' | 'desc' | undefined
  }>({
    field: undefined,
    direction: undefined
  })

  const [selectedManageFilter, setSelectedManageFilter] = useState(manageFilters[0])

  const [selectedManageFilterStaff, setSelectedManageFilterStaff] = useState(manageFiltersStaff[0])

  const userStdCode = useMemo(
    () => authAtomValue?.user?.std_code ?? 0,
    [authAtomValue?.user?.std_code]
  )

  const [appliedOrderBy, setAppliedOrderBy] = useState<'user_name' | 'total_stores' | undefined>()

  const [appliedOrderByDirection, setAppliedOrderByDirection] = useState<
    'asc' | 'desc' | undefined
  >()

  const {
    isOpen: manageWalletModalIsOpen,
    onClose: manageWalletModalOnClose,
    onOpen: manageWalletModalOnOpen
  } = useDisclose()

  const {
    data: staffListData,
    isLoading: staffListIsLoading,
    isFetchingNextPage: staffListIsFetchingNextPage,
    hasNextPage: staffListHasNextPage,
    fetchNextPage: staffListFetchNextPage
  } = useGetStaffListInfinityQuery({
    leader_std_code: leaderStdCode ? Number(leaderStdCode) : userStdCode,
    order_by: appliedOrderBy,
    order_by_direction: appliedOrderByDirection,
    ...(debouncedSearchStaff?.length && { search: debouncedSearchStaff }),
    ...(debouncedSearchManageStaff?.length && { search: debouncedSearchManageStaff }),
    ...(manageWalletModalIsOpen && {
      assigned_users:
        selectedManageFilterStaff.id !== 0 ? selectedManageFilterStaff.id === 1 : undefined
    })
  })

  const staffData = useMemo(() => mergeStaffInfiniteQuery(staffListData), [staffListData])

  const staffTotalItems = staffListData?.pages[0].data.metadata.pagination.total ?? 0

  const onEndReachedStaff = useCallback(() => {
    if (!staffListIsFetchingNextPage && staffListHasNextPage) {
      staffListFetchNextPage()
    }
  }, [staffListIsFetchingNextPage, staffListHasNextPage, staffListFetchNextPage])

  useEffect(() => {
    handleSearchClear()
  }, [manageWalletModalIsOpen])

  const {
    data: storesListData,
    isLoading: storesListIsLoading,
    isFetchingNextPage: storesListIsFetchingNextPage,
    hasNextPage: storesListHasNextPage,
    fetchNextPage: storesListFetchNextPage
  } = useGetStoresListInfinityQuery({
    leader_std_code: leaderStdCode ? Number(leaderStdCode) : userStdCode,
    assigned_stores: manageWalletModalIsOpen
      ? selectedManageFilter.id !== 0
        ? selectedManageFilter.id === 1
        : undefined
      : currentTab === TAB_ENUM.AssignedTabs,
    ...(debouncedSearchStore?.length && { search: debouncedSearchStore }),
    ...(debouncedSearchManageStore?.length && { search: debouncedSearchManageStore })
  })

  const storesData = useMemo(() => mergeStoresInfiniteQuery(storesListData), [storesListData])

  const storesTotalItems = storesListData?.pages[0].data.metadata.pagination.total ?? 0

  const onEndReachedStores = useCallback(() => {
    if (!storesListIsFetchingNextPage && storesListHasNextPage) {
      storesListFetchNextPage()
    }
  }, [storesListIsFetchingNextPage, storesListHasNextPage, storesListFetchNextPage])

  const { data: walletsManagementDetailsData, isLoading: walletsManagementDetailsIsLoading } =
    useGetWalletsManagementDetailsQuery({
      leader_std_code: leaderStdCode ? Number(leaderStdCode) : userStdCode
    })

  const notInManagement = !walletsManagementDetailsData?.status

  const detailsData = walletsManagementDetailsData?.data.data

  const { mutate: concludeMutate, isLoading: concludeIsLoading } =
    useConcludeWalletsManagementMutation()

  const { mutate: manageWalletMutate, isLoading: manageWalletIsLoading } = useManageWalletMutation()

  const { mutate: flagAsExternalMutate, isLoading: flagAsExternalIsLoading } =
    useFlagAsExternalMutation()

  const handleTabChange = (tab: TAB_ENUM) => {
    setCurrentTab(tab)
  }

  const {
    isOpen: concludeModalIsOpen,
    onClose: concludeModalOnClose,
    onOpen: concludeModalOnOpen
  } = useDisclose()

  const {
    isOpen: confirmManageWalletModalIsOpen,
    onClose: confirmManageWalletModalOnClose,
    onOpen: confirmManageWalletModalOnOpen
  } = useDisclose()

  const {
    isOpen: reactivateModalIsOpen,
    onClose: reactivateModalOnClose,
    onOpen: reactivateModalOnOpen
  } = useDisclose()

  const toast = useToast()

  const handleConclude = useCallback(() => {
    const clearAllTimeouts = () => {
      timeouts.forEach((timeoutId) => clearTimeout(timeoutId))

      setTimeouts([])
    }

    concludeMutate(
      { leader_std_code: leaderStdCode ? Number(leaderStdCode) : userStdCode, stores: undefined },
      {
        onError() {
          const timeoutId = window.setTimeout(() => {
            concludeModalOnClose()

            clearAllTimeouts()
          }, 1000)

          setTimeouts((prev) => [...prev, timeoutId])

          toast.show({
            render: () => <Toast type="error" text="Não foi possível completar a ação " />,
            placement: 'bottom',
            duration: 1500
          })
        },
        onSuccess() {
          const timeoutId = window.setTimeout(() => {
            concludeModalOnClose()

            clearAllTimeouts()
          }, 1000)

          setTimeouts((prev) => [...prev, timeoutId])

          toast.show({
            render: () => <Toast type="success" text="Carteirização concluída 🎉" />,
            placement: 'bottom',
            duration: 1500
          })
        }
      }
    )
  }, [concludeMutate, concludeModalOnClose, leaderStdCode, userStdCode, toast, timeouts])

  const handleFlagAsExternal = useCallback(
    (tab_number: number, status: boolean) => {
      const clearAllTimeouts = () => {
        timeouts.forEach((timeoutId) => clearTimeout(timeoutId))

        setTimeouts([])
      }

      flagAsExternalMutate(
        {
          leaderStdCode: leaderStdCode ? Number(leaderStdCode) : userStdCode,
          storeTabNumber: tab_number,
          status
        },
        {
          onError() {
            const timeoutId = window.setTimeout(() => {
              clearAllTimeouts()
            }, 1000)

            setTimeouts((prev) => [...prev, timeoutId])

            toast.show({
              render: () => <Toast type="error" text="Não foi possível completar a ação " />,
              placement: 'bottom',
              duration: 1500
            })
          },
          onSuccess() {
            const timeoutId = window.setTimeout(() => {
              clearAllTimeouts()
            }, 1000)

            setTimeouts((prev) => [...prev, timeoutId])

            toast.show({
              render: () => <Toast type="success" text="Atualização efetuada com sucesso 🎉" />,
              placement: 'bottom',
              duration: 1500
            })
          }
        }
      )
    },
    [leaderStdCode, flagAsExternalMutate, timeouts, toast, userStdCode]
  )

  const { isOpen: orderByIsOpen, onOpen: orderByOnOpen, onClose: orderByOnClose } = useDisclose()

  const handleCheckboxChange = (field: string, direction: 'asc' | 'desc') => {
    setSelectedOrderBy({ field, direction })
  }

  const handleSelectManageFilter = (filter: { id: number; name: string }) => {
    setSelectedManageFilter(filter)
  }

  const handleSelectManageFilterStaff = (filter: { id: number; name: string }) => {
    setSelectedManageFilterStaff(filter)
  }

  const handleApply = () => {
    setAppliedOrderBy(
      selectedOrderBy?.field
        ? selectedOrderBy?.field === 'Nome'
          ? 'user_name'
          : 'total_stores'
        : undefined
    )

    setAppliedOrderByDirection(selectedOrderBy?.direction)
  }

  const handleClear = () => {
    setSelectedOrderBy({ field: undefined, direction: undefined })
  }

  useEffect(() => {
    return () => {
      timeouts.forEach((timeoutId) => clearTimeout(timeoutId))
    }
  }, [timeouts])

  const unassignedStores = (detailsData?.tabs?.total ?? 0) - (detailsData?.tabs?.assigned ?? 0)

  const handleGoToConclude = useCallback(() => {
    concludeModalOnClose()

    linkTo(`/carteirizacao/concluir/${leaderStdCode ?? userStdCode}`)
  }, [linkTo, leaderStdCode, userStdCode, concludeModalOnClose])

  const walletManagementIsOpen =
    detailsData?.status === WALLET_MANAGEMENT_STATUS_ENUM.ACTIVED ||
    detailsData?.status === WALLET_MANAGEMENT_STATUS_ENUM.REACTIVED

  const handleGoBack = () => linkTo('/carteirizacao-admin')

  const handleManageWallet = useCallback(
    (item: TManageWalletParams) => {
      const clearAllTimeouts = () => {
        timeouts.forEach((timeoutId) => clearTimeout(timeoutId))

        setTimeouts([])
      }

      manageWalletMutate(
        {
          leaderStdCode: leaderStdCode ? Number(leaderStdCode) : userStdCode,
          storesTabNumber: item.storesTabNumber,
          userStdCode: item.userStdCode
        },
        {
          onError() {
            const timeoutId = window.setTimeout(() => {
              clearAllTimeouts()
            }, 1000)

            setTimeouts((prev) => [...prev, timeoutId])

            confirmManageWalletModalOnClose()

            resetSelectedItems()

            toast.show({
              render: () => <Toast type="error" text="Não foi possível completar a ação " />,
              placement: 'bottom',
              duration: 1500
            })
          },
          onSuccess() {
            const timeoutId = window.setTimeout(() => {
              clearAllTimeouts()
            }, 1000)

            setTimeouts((prev) => [...prev, timeoutId])

            confirmManageWalletModalOnClose()

            resetSelectedItems()

            toast.show({
              render: () => <Toast type="success" text="Carteirização concluída 🎉" />,
              placement: 'bottom',
              duration: 1500
            })
          }
        }
      )
    },
    [
      manageWalletMutate,
      leaderStdCode,
      toast,
      timeouts,
      confirmManageWalletModalOnClose,
      userStdCode
    ]
  )

  const handleGoToOpenWalletsPeriod = useCallback(() => {
    navigation.navigate('OpenWalletsPeriod', {
      leaderStdCode: String(leaderStdCode)
    })
  }, [navigation, leaderStdCode])

  const [selectedTabs, setSelectedTabs] = useState<Set<number>>(new Set())

  const handleToggleTab = (tabId: number) => {
    setSelectedTabs((prevSelectedTabs) => {
      const newSelectedTabs = new Set(prevSelectedTabs)

      if (newSelectedTabs.has(tabId)) {
        newSelectedTabs.delete(tabId)
      } else {
        newSelectedTabs.add(tabId)
      }

      return newSelectedTabs
    })
  }

  const [selectedStaff, setSelectedStaff] = useState<Partial<TStaffListItem> | undefined>()

  const handleSelectStaff = (item: Partial<TStaffListItem>) => {
    setSelectedStaff((prevStaffId) =>
      prevStaffId?.user_std_code === item.user_std_code ? undefined : item
    )
  }

  const resetSelectedItems = () => {
    setSelectedStaff(undefined)

    setSelectedTabs(new Set())
  }

  const { mutate: reactivateMutate, isLoading: reactivateIsLoading } =
    useReactivateWalletsPeriodMutation()

  const handleReactivate = useCallback(() => {
    const clearAllTimeouts = () => {
      timeouts.forEach((timeoutId) => clearTimeout(timeoutId))

      setTimeouts([])
    }

    reactivateMutate(
      {
        leaderStdCode: leaderStdCode ? Number(leaderStdCode) : userStdCode
      },
      {
        onError() {
          const timeoutId = window.setTimeout(() => {
            clearAllTimeouts()
          }, 1000)

          setTimeouts((prev) => [...prev, timeoutId])

          reactivateModalOnClose()

          toast.show({
            render: () => <Toast type="error" text="Não foi possível completar a ação " />,
            placement: 'bottom',
            duration: 1500
          })
        },
        onSuccess() {
          const timeoutId = window.setTimeout(() => {
            clearAllTimeouts()
          }, 1000)

          reactivateModalOnClose()

          setTimeouts((prev) => [...prev, timeoutId])

          toast.show({
            render: () => <Toast type="success" text="Atualização efetuada com sucesso 🎉" />,
            placement: 'bottom',
            duration: 1500
          })
        }
      }
    )
  }, [leaderStdCode, reactivateMutate, timeouts, toast, userStdCode, reactivateModalOnClose])

  const { data: walletsManagementReportData, isLoading: walletsManagementReportIsLoading } =
    useGetWalletsManagementReportQuery({
      leader_std_code: leaderStdCode ? Number(leaderStdCode) : userStdCode,
      enabled:
        detailsData?.status === WALLET_MANAGEMENT_STATUS_ENUM.CONCLUDED ||
        detailsData?.status === WALLET_MANAGEMENT_STATUS_ENUM.CLOSED
    })

  const { mutateAsync: saveFileMutationAsync, isLoading: saveFileIsLoading } =
    useSaveReportFileMutation()

  const { mutateAsync: uploadMutationAsync, isLoading: isUploadLoading } = useUploadMutation()

  const reportData = walletsManagementReportData?.data.data

  const [downloadReportIsLoading, setDownloadReportIsLoading] = useState(false)

  const handleDownloadReport = useCallback(async () => {
    setDownloadReportIsLoading(true)

    if (!reportData?.file_url) {
      try {
        const blob = await pdf(<ReportPDF {...reportData} />).toBlob()

        const file = blob

        if (file.type && file.type.split('/').length > 0) {
          const type = file.type.split('/')[1]

          const files = await uploadMutationAsync({
            directory_type: DIRECTORY_TYPE_ENUM.WALLETS_MANAGEMENT_REPORTS,
            extension_type: type.toUpperCase() as keyof typeof EXTENSION_TYPE_ENUM,
            files: [file]
          })

          if (files.length) {
            if (files[0].status !== 200) {
              console.log('erro')

              return
            }

            const fileName = files[0].name

            saveFileMutationAsync({
              leaderStdCode: leaderStdCode ? Number(leaderStdCode) : userStdCode,
              fileName
            })
          }
        }

        saveAs(file, 'Relatorio Carteirizacao.pdf')
      } catch (error) {
        console.error('Erro ao gerar o PDF:', error)
      }
    } else {
      const response = await fetch(reportData?.file_url)

      const file = await response.blob()

      saveAs(file, 'Relatorio Carteirizacao.pdf')
    }

    setDownloadReportIsLoading(false)
  }, [reportData, userStdCode, leaderStdCode, saveFileMutationAsync, uploadMutationAsync])

  const anyHookIsLoading =
    staffListIsLoading ||
    storesListIsLoading ||
    walletsManagementDetailsIsLoading ||
    concludeIsLoading ||
    manageWalletIsLoading ||
    flagAsExternalIsLoading ||
    reactivateIsLoading ||
    walletsManagementReportIsLoading ||
    isUploadLoading ||
    saveFileIsLoading ||
    downloadReportIsLoading

  return {
    isMobile,
    currentTab,
    staffData,
    staffListIsLoading,
    storesData,
    storesListIsLoading,
    detailsData,
    detailsIsLoading: walletsManagementDetailsIsLoading,
    handleTabChange,
    concludeModalIsOpen,
    concludeModalOnClose,
    concludeModalOnOpen,
    handleConclude,
    concludeIsLoading,
    searchManageStaff,
    onSearchManageStaff,
    searchManageStore,
    onSearchManageStore,
    searchStaff,
    onSearchStaff,
    searchStore,
    onSearchStore,
    onPressSearchClear: handleSearchClear,
    handleClear,
    handleApply,
    handleCheckboxChange,
    selectedOrderBy,
    orderByIsOpen,
    orderByOnOpen,
    orderByOnClose,
    manageWalletModalIsOpen,
    manageWalletModalOnClose,
    manageWalletModalOnOpen,
    unassignedStores,
    handleGoToConclude,
    walletManagementIsOpen,
    isAdmin,
    handleGoBack,
    handleManageWallet,
    confirmManageWalletModalIsOpen,
    confirmManageWalletModalOnClose,
    confirmManageWalletModalOnOpen,
    manageWalletIsLoading,
    reactivateModalIsOpen,
    reactivateModalOnClose,
    reactivateModalOnOpen,
    handleGoToOpenWalletsPeriod,
    onEndReachedStaff,
    onEndReachedStores,
    staffTotalItems,
    storesTotalItems,
    staffListIsFetchingNextPage,
    storesListIsFetchingNextPage,
    manageFilters,
    selectedManageFilter,
    handleSelectManageFilter,
    manageFiltersStaff,
    selectedManageFilterStaff,
    handleSelectManageFilterStaff,
    handleFlagAsExternal,
    flagAsExternalIsLoading,
    handleSelectStaff,
    handleToggleTab,
    selectedTabs,
    selectedStaff,
    notInManagement,
    handleReactivate,
    reactivateIsLoading,
    anyHookIsLoading,
    handleDownloadReport,
    walletsManagementReportIsLoading
  }
}
